/**
 * Maps backend currency-guard error codes to pt-BR user messages + recovery hints.
 *
 * AP-specific codes mirror `apps/api/src/modules/ap/bill/ap-currency.errors.ts` — keep
 * them in sync. GL choke-point codes (`REPORTING_FUNCTIONAL_CURRENCY_DIVERGENCE`,
 * `JOURNAL_CURRENCY_BATCH_INVALID`) mirror the inline payloads thrown from
 * `apps/api/src/modules/gl/journal/journal.service.ts` and
 * `apps/api/src/modules/gl/book/book.service.ts`.
 *
 * The server already returns `userMessage`/`recoveryHint`; this map is the client-side
 * fallback, consumed both by the AP bill-detail recovery flow (set currency/exchange
 * rate, then retry) and generically by `getErrorMessage` (`@/api/errors`) for any
 * other form that surfaces a currency-guard error without bespoke handling.
 */

export interface CurrencyErrorCopy {
  userMessage: string;
  recoveryHint: string;
}

const TEMPLATES: Record<string, CurrencyErrorCopy> = {
  AP_BILL_FOREIGN_CURRENCY_NO_RATE: {
    userMessage:
      'Esta nota está em moeda estrangeira ({{currencyCode}}) e ainda não possui taxa de câmbio para conversão em BRL.',
    recoveryHint:
      'Informe a taxa de câmbio da nota (ou confirme a moeda correta) e tente novamente. Lançamentos contábeis são sempre feitos em BRL.',
  },
  AP_PAYMENT_FOREIGN_CURRENCY_RATE_REQUIRED: {
    userMessage:
      'Esta nota está em moeda estrangeira ({{currencyCode}}) e não pode ser marcada como paga sem a taxa de câmbio da liquidação em BRL.',
    recoveryHint:
      'Concilie o pagamento pelo extrato bancário/cartão (que traz o valor em BRL) ou informe a taxa de câmbio da nota antes de marcar como paga.',
  },
  AP_PAYMENT_FOREIGN_SCHEDULE_ALLOCATION_REQUIRED: {
    userMessage:
      'O valor programado desta conta em moeda estrangeira só pode ser alterado pelo fluxo que recalcula a alocação em moeda estrangeira.',
    recoveryHint:
      'Recalcule o cronograma de pagamento da conta para manter os valores em moeda funcional e estrangeira consistentes.',
  },
  AP_BILL_CURRENCY_REVIEW_REQUIRED: {
    userMessage:
      'A moeda extraída deste documento ({{rawCurrency}}) não foi reconhecida e precisa ser confirmada antes de continuar.',
    recoveryHint:
      'Revise a moeda da nota e salve uma opção suportada. A confirmação ficará registrada no histórico de ingestão.',
  },
  REPORTING_FUNCTIONAL_CURRENCY_DIVERGENCE: {
    userMessage:
      'Não é possível salvar: a moeda de apresentação do livro contábil diverge da moeda funcional já em uso.',
    recoveryHint: 'Para operar em outra moeda, crie um novo livro contábil com a moeda desejada.',
  },
  JOURNAL_CURRENCY_BATCH_INVALID: {
    userMessage:
      'Não foi possível registrar o lançamento contábil devido a uma inconsistência na moeda dos lançamentos.',
    recoveryHint:
      'Tente novamente em alguns instantes. Se o problema persistir, contate o suporte.',
  },
};

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => {
    const value = params[key];
    return value === undefined || value === null ? `{{${key}}}` : String(value);
  });
}

const EXTRACTED_CURRENCY_REVIEW_MESSAGE = /^Extracted currency "([^"]+)" requires confirmation$/;

/** Extracts the unrecognized currency token from readiness-validation `errors[].message`. */
export function extractRawCurrencyFromReviewMessage(message: string): string | null {
  const match = message.match(EXTRACTED_CURRENCY_REVIEW_MESSAGE);
  return match?.[1] ?? null;
}

function hasUnresolvedTemplatePlaceholders(text: string): boolean {
  return /\{\{\w+\}\}/.test(text);
}

/** Client-side fallback copy for a currency-guard error code. */
export function getCurrencyErrorCopy(
  code: string,
  params?: Record<string, string | number>,
): CurrencyErrorCopy | null {
  const entry = TEMPLATES[code];
  if (!entry) return null;
  return {
    userMessage: interpolate(entry.userMessage, params),
    recoveryHint: interpolate(entry.recoveryHint, params),
  };
}

/**
 * Currency-guard copy for a structured validation error, interpolating template
 * params parsed from the server message when available.
 */
export function getCurrencyErrorCopyFromValidation(
  code: string,
  validationMessage?: string,
): CurrencyErrorCopy | null {
  const rawCurrency = validationMessage
    ? extractRawCurrencyFromReviewMessage(validationMessage)
    : null;
  return getCurrencyErrorCopy(code, rawCurrency ? { rawCurrency } : undefined);
}

/** Prefer interpolated currency copy; fall back to a mapped validation message when templates are unresolved. */
export function resolveCurrencyValidationToastMessage(
  code: string,
  validationMessage: string | undefined,
  mappedValidationMessage: string,
): Pick<CurrencyErrorCopy, 'userMessage' | 'recoveryHint'> | null {
  const copy = getCurrencyErrorCopyFromValidation(code, validationMessage);
  if (!copy) return null;
  return {
    userMessage: hasUnresolvedTemplatePlaceholders(copy.userMessage)
      ? mappedValidationMessage
      : copy.userMessage,
    recoveryHint: copy.recoveryHint,
  };
}

/** Extract a structured currency-guard payload from an API error, if present. */
export function extractCurrencyErrorPayload(
  err: unknown,
): { code: string; userMessage?: string; recoveryHint?: string } | null {
  const maybeError = err as { response?: { data?: unknown }; details?: unknown };
  const data = (maybeError?.response?.data ?? maybeError?.details) as
    | {
        code?: string;
        message?: string | { code?: string; userMessage?: string; recoveryHint?: string };
        userMessage?: string;
        recoveryHint?: string;
        details?: unknown;
      }
    | undefined;
  if (!data) return null;

  if (typeof data.message === 'object' && data.message) {
    const inner = data.message;
    if (inner.code && inner.code in TEMPLATES) {
      return { code: inner.code, userMessage: inner.userMessage, recoveryHint: inner.recoveryHint };
    }
  }
  if (data.code && data.code in TEMPLATES) {
    return { code: data.code, userMessage: data.userMessage, recoveryHint: data.recoveryHint };
  }
  if (typeof data.message === 'object' && data.message) {
    const nestedDetails = data.message as { details?: unknown };
    if (nestedDetails.details)
      return extractCurrencyErrorPayload({ details: nestedDetails.details });
  }
  if (data.details) {
    return extractCurrencyErrorPayload({ details: data.details });
  }
  return null;
}
